import { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Button, Space, Typography, Alert, Tooltip, Modal, message,
  Drawer, Form, Switch, Popconfirm, Tag, Spin, Empty,
} from 'antd';
import {
  ReloadOutlined, AudioOutlined, PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import {
  getAvailablePhraseGroups, createPhraseGroup, updatePhraseGroup,
  getTermsForGroup, addTermToGroup, updateTerm, removeTermFromGroup,
  type PhraseGroupView, type PhraseTermView,
} from '@/api/phraseLists';

const { Text } = Typography;

// Merchant-scoped library of STT phrase buckets (global + merchant). Each row is
// a bucket; "Terms" opens a drawer to add/edit/enable/remove its terms. Buckets
// are attached to agents from the agent detail page's Phrase Lists tab.
// Self-contained (local state + the api/phraseLists module) — no MobX store.
export function PhraseListLibraryPage() {
  const [groups, setGroups] = useState<PhraseGroupView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PhraseGroupView | null>(null);
  const [termsFor, setTermsFor] = useState<PhraseGroupView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setGroups(await getAvailablePhraseGroups());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load phrase buckets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = groups.filter((g) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (g.title ?? '').toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q);
  });

  const columns: ColumnsType<PhraseGroupView> = [
    {
      title: 'Bucket',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
      render: (text: string, row) => (
        <div>
          <Text strong style={{ fontSize: 13.5 }}>{text}</Text>
          {row.merchantId == null && <span style={{ marginLeft: 8 }}><StatusPill tone="violet">Global</StatusPill></span>}
          {!row.isActive && <span style={{ marginLeft: 8 }}><StatusPill tone="slate">Inactive</StatusPill></span>}
          {row.description && (
            <div style={{ marginTop: 3 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Terms',
      key: 'terms',
      width: 120,
      render: (_t, row) => <StatusPill tone="ink">{row.termCount} term{row.termCount === 1 ? '' : 's'}</StatusPill>,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (v: number) => <Text type="secondary" style={{ fontSize: 11 }}>#{v}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      align: 'right',
      render: (_t, row) => (
        <Space size={4}>
          <Button size="small" icon={<TagsOutlined />} onClick={() => setTermsFor(row)}>
            Terms
          </Button>
          <Tooltip title="Edit bucket">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(row)} aria-label="Edit bucket" />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<AudioOutlined />}
        title="Phrase Lists"
        subtitle="Speech-to-text vocabulary buckets for voice agents"
        count={groups.length}
        countLabel={groups.length === 1 ? 'bucket' : 'buckets'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search buckets…"
              allowClear
              style={{ width: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreateOpen(true); trackEvent('PhraseList.CreateOpened'); }}>
              New bucket
            </Button>
          </Space>
        }
      />

      {error && <Alert type="error" message="Failed to load phrase buckets" description={error} showIcon closable />}

      <SectionCard noPadding>
        <Table<PhraseGroupView>
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true, style: { padding: '12px 18px', margin: 0 } }}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<AudioOutlined />}
                title={search ? 'No buckets match' : 'No phrase buckets yet'}
                description={
                  search
                    ? 'Try clearing the search.'
                    : 'Create a bucket of speech-recognition terms (drug names, regimens, symptoms) to bias your voice agents toward the right vocabulary.'
                }
                action={
                  !search ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                      Create bucket
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
        />
      </SectionCard>

      <BucketFormModal
        open={createOpen || !!editing}
        bucket={editing}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={() => { setCreateOpen(false); setEditing(null); void load(); }}
      />

      <ManageTermsDrawer
        bucket={termsFor}
        onClose={() => setTermsFor(null)}
        onChanged={() => void load()}
      />
    </div>
  );
}

// ── Create / edit bucket (small 2-3 field form → Modal is appropriate) ────────

function BucketFormModal({
  open, bucket, onClose, onSaved,
}: { open: boolean; bucket: PhraseGroupView | null; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!bucket;

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        title: bucket?.title ?? '',
        description: bucket?.description ?? '',
        isActive: bucket?.isActive ?? true,
      });
    }
  }, [open, bucket, form]);

  async function onOk() {
    let v: { title: string; description?: string; isActive: boolean };
    try {
      v = await form.validateFields();
    } catch {
      return; // validation errors shown inline
    }
    setSaving(true);
    try {
      if (isEdit && bucket) {
        await updatePhraseGroup(bucket.id, v.title, v.description, v.isActive);
        message.success('Bucket updated');
      } else {
        await createPhraseGroup(v.title, v.description);
        message.success('Bucket created');
        trackEvent('PhraseList.BucketCreated');
      }
      onSaved();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to save bucket');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit phrase bucket' : 'New phrase bucket'}
      onCancel={onClose}
      onOk={() => void onOk()}
      confirmLoading={saving}
      okText={isEdit ? 'Save' : 'Create'}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input placeholder="e.g. FOLFOX regimen" maxLength={200} />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="What vocabulary this bucket covers" maxLength={500} />
        </Form.Item>
        {isEdit && (
          <Form.Item name="isActive" label="Active" valuePropName="checked" tooltip="Inactive buckets resolve no terms for any agent.">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

// ── Manage terms in a bucket (richer flow → Drawer, per project convention) ───

function ManageTermsDrawer({
  bucket, onClose, onChanged,
}: { bucket: PhraseGroupView | null; onClose: () => void; onChanged: () => void }) {
  const open = !!bucket;
  const [terms, setTerms] = useState<PhraseTermView[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!bucket) return;
    setLoading(true);
    try {
      setTerms(await getTermsForGroup(bucket.id));
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to load terms');
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => { if (open) { void load(); form.resetFields(); } }, [open, load, form]);

  async function onAdd() {
    if (!bucket) return;
    let v: { term: string; description?: string; languageCode?: string };
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    setAdding(true);
    try {
      await addTermToGroup(bucket.id, v.term, v.description, v.languageCode);
      form.resetFields();
      await load();
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to add term');
    } finally {
      setAdding(false);
    }
  }

  async function onRemove(t: PhraseTermView) {
    if (!bucket) return;
    try {
      await removeTermFromGroup(bucket.id, t.termId);
      await load();
      onChanged();
      message.success(`Removed "${t.term}"`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to remove term');
    }
  }

  async function onToggleActive(t: PhraseTermView) {
    try {
      await updateTerm(t.termId, t.term, t.description ?? undefined, t.languageCode ?? undefined, !t.isActive);
      await load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to update term');
    }
  }

  const columns: ColumnsType<PhraseTermView> = [
    {
      title: 'Term',
      dataIndex: 'term',
      key: 'term',
      render: (text: string, row) => (
        <div>
          <Text style={{ fontSize: 13 }}>{text}</Text>
          {row.languageCode && <Tag style={{ marginLeft: 8, fontSize: 10 }}>{row.languageCode}</Tag>}
          {!row.isActive && <Tag style={{ marginLeft: 6, fontSize: 10 }}>off</Tag>}
          {row.description && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{row.description}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      align: 'right',
      render: (_t, row) => (
        <Space size={2}>
          <Button type="text" size="small" onClick={() => void onToggleActive(row)}>
            {row.isActive ? 'Disable' : 'Enable'}
          </Button>
          <Popconfirm
            title="Remove this term from the bucket?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onRemove(row)}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} aria-label="Remove term" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="46%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <TagsOutlined style={{ color: colors.ink[600], fontSize: 18 }} />
          <span>Terms — {bucket?.title}</span>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Space align="end" wrap>
            <Form.Item name="term" label="Add term" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
              <Input placeholder="e.g. oxaliplatin" style={{ width: 220 }} maxLength={200} />
            </Form.Item>
            <Form.Item name="languageCode" label="Lang" style={{ marginBottom: 0 }} tooltip="BCP-47 language subtag (e.g. es). Blank = language-neutral.">
              <Input placeholder="(neutral)" style={{ width: 90 }} maxLength={10} />
            </Form.Item>
            <Form.Item name="description" label="Description" style={{ marginBottom: 0 }}>
              <Input placeholder="optional" style={{ width: 200 }} maxLength={500} />
            </Form.Item>
            <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={() => void onAdd()}>
              Add
            </Button>
          </Space>
        </Form>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : terms.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No terms yet — add the first one above." />
        ) : (
          <Table<PhraseTermView> rowKey="itemId" columns={columns} dataSource={terms} pagination={false} size="small" />
        )}
      </div>
    </Drawer>
  );
}
